import React, { useState, useEffect } from "react";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const PayoutWeekdayDistribution = () => {
  const [loading, setLoading] = useState(true);
  const [distribution, setDistribution] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDistribution();
  }, []);

  const fetchDistribution = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      if (!token) {
        window.location.href = "/login";
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/payout-weekday-distribution`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setDistribution(response.data.data);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load weekday distribution",
      );
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  const capitalizeWeekday = (weekday) => {
    if (!weekday) return "";
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  };

  const getBarColor = (weekday) => {
    const colors = {
      monday: "#3b82f6",
      tuesday: "#8b5cf6",
      wednesday: "#10b981",
      thursday: "#f59e0b",
      friday: "#ef4444",
      saturday: "#6366f1",
      sunday: "#ec4899",
      unassigned: "#9ca3af",
    };
    return colors[weekday] || "#6b7280";
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            fontSize: "16px",
            color: "#6b7280",
          }}
        >
          Loading weekday distribution...
        </div>
      </PortalAdminLayout>
    );
  }

  if (error) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: "8px",
              color: "#b91c1c",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            📅 Payout Weekday Distribution
          </h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Rolling weekly payout schedule distribution across all beneficiaries
          </p>
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              Total Beneficiaries
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#1f2937" }}
            >
              {distribution?.totals.beneficiaries.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              Staff (R3 + R4)
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#3b82f6" }}
            >
              {distribution?.totals.staff.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              Business (R1)
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#10b981" }}
            >
              {distribution?.totals.business.toLocaleString()}
            </div>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              Unassigned Weekday
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color:
                  distribution?.totals.unassigned.total > 0
                    ? "#f59e0b"
                    : "#9ca3af",
              }}
            >
              {distribution?.totals.unassigned.total.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Weekday Distribution Chart */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
              marginBottom: "20px",
            }}
          >
            Distribution by Weekday
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {distribution?.weekdayStats.map((stat) => (
              <div key={stat.weekday}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      minWidth: "100px",
                    }}
                  >
                    {capitalizeWeekday(stat.weekday)}
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {stat.total.count} ({stat.total.percentage}%)
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "32px",
                    background: "#f3f4f6",
                    borderRadius: "6px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${stat.total.percentage}%`,
                      background: getBarColor(stat.weekday),
                      borderRadius: "6px",
                      transition: "width 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {parseFloat(stat.total.percentage) > 5 &&
                      `${stat.total.percentage}%`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Breakdown Table */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            overflowX: "auto",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
              marginBottom: "20px",
            }}
          >
            Detailed Breakdown
          </h2>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Weekday
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Staff Count
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Staff %
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Business Count
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Business %
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Total
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px 8px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Total %
                </th>
              </tr>
            </thead>
            <tbody>
              {distribution?.weekdayStats.map((stat, index) => (
                <tr
                  key={stat.weekday}
                  style={{
                    borderBottom: "1px solid #f3f4f6",
                    background:
                      stat.weekday === "unassigned" ? "#fef3c7" : "transparent",
                  }}
                >
                  <td
                    style={{
                      padding: "12px 8px",
                      fontWeight: "500",
                      color:
                        stat.weekday === "unassigned" ? "#92400e" : "#374151",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "3px",
                          background: getBarColor(stat.weekday),
                        }}
                      />
                      {capitalizeWeekday(stat.weekday)}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      color: "#6b7280",
                    }}
                  >
                    {stat.staff.count.toLocaleString()}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      color: "#6b7280",
                    }}
                  >
                    {stat.staff.percentage}%
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      color: "#6b7280",
                    }}
                  >
                    {stat.business.count.toLocaleString()}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      color: "#6b7280",
                    }}
                  >
                    {stat.business.percentage}%
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    {stat.total.count.toLocaleString()}
                  </td>
                  <td
                    style={{
                      padding: "12px 8px",
                      textAlign: "right",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    {stat.total.percentage}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Info Section */}
        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            background: "#f0f9ff",
            border: "1px solid #bae6fd",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#0c4a6e",
            lineHeight: "1.6",
          }}
        >
          <strong>ℹ️ About Rolling Weekly Payouts:</strong>
          <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
            <li>
              Each beneficiary is assigned a specific weekday for automatic
              payouts
            </li>
            <li>
              Payouts are processed daily at 2 AM for beneficiaries with that
              weekday
            </li>
            <li>
              Weekday assignment uses a hash distribution for even load
              balancing
            </li>
            <li>
              Beneficiaries can change their payout weekday with a 30-day
              cooldown
            </li>
            <li>
              Unassigned beneficiaries will be automatically assigned a weekday
              on first access
            </li>
          </ul>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default PayoutWeekdayDistribution;
